import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  Building2,
  LogOut,
  ChevronDown,
  Lock,
  Sun,
  Moon,
  LayoutDashboard,
} from 'lucide-react';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import NotificationBell from '@/components/NotificationBell';
import { CRM_SECTIONS } from '@/pages/CRMMaster';
import { getCrmRoutePath, getCurrentAdminSurfaceContext } from '@/lib/paths';
import { buildMasterUrl } from '@easyflow/shared';

// Task #3055 — EasyCRM shell. Topbar brandizzata EasyCRM + sidebar laterale con
// le 10 sezioni CRM (SoT in CRM_SECTIONS). Rimpiazza i <Tabs> interni del CRM.
// Il launcher "Console operativa" è visibile SOLO al super_admin (il ruolo
// "admin_crm" vive esclusivamente dentro EasyCRM).
export default function EasyCrmLayout(): JSX.Element {
  const navigate = useNavigate();
  const { section } = useParams<{ section?: string }>();
  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const surfaceContext = getCurrentAdminSurfaceContext();
  const crmSectionPath = (sectionName: string): string => getCrmRoutePath(sectionName);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('master-theme') as 'dark' | 'light') || 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('master-theme', theme);
  }, [theme]);

  const toggleTheme = (): void => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const handleLogout = async (): Promise<void> => {
    await logout();
  };

  const activeSection = section ?? 'azienda';

  const u = user as { firstName?: string; lastName?: string; email?: string } | null;
  const first = (u?.firstName ?? '').trim();
  const last = (u?.lastName ?? '').trim();
  const email = (u?.email ?? '').trim();
  const fullName = first || last
    ? `${first}${first && last ? ' ' : ''}${last}`
    : (email || 'EasyCRM');
  let initials: string;
  if (first && last) initials = (first[0] ?? '') + (last[0] ?? '');
  else if (first) initials = first.slice(0, 2);
  else if (email) {
    const local = email.split('@')[0] ?? '';
    initials = local.length >= 2 ? local.slice(0, 2) : (local + 'C').slice(0, 2);
  } else initials = 'CR';
  initials = initials.toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      {/* Topbar */}
      <header
        className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-border dark:border-white/10 shadow-sm transition-colors duration-300"
        role="banner"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate(crmSectionPath('azienda'))}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-xl"
              aria-label="EasyCRM"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div className="hidden sm:block leading-tight text-left">
                <h1 className="text-base font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 dark:from-blue-400 to-sky-600 dark:to-sky-400">
                  EasyCRM
                </h1>
                <p className="text-[9px] text-blue-600/70 dark:text-blue-300/70 -mt-0.5 tracking-wide">
                  by EasyDigitalGroup
                </p>
              </div>
            </button>

            <div className="flex items-center gap-1 sm:gap-2">
              {isSuperAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (surfaceContext.surface === 'crm') {
                      window.location.assign(buildMasterUrl('/dashboard'));
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                  className="hidden sm:inline-flex items-center gap-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-white/10"
                  title="Vai alla console operativa Master"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Console operativa</span>
                </Button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-muted-foreground dark:text-muted-foreground/60 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-white/10"
                title={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'}
                aria-label={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'}
                aria-pressed={theme === 'dark'}
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <NotificationBell />

              <div className="hidden sm:block h-8 w-px bg-gray-300 dark:bg-white/20 mx-2" />

              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className={cn(
                    'flex items-center gap-2 rounded-full p-0.5 pr-2 transition-all',
                    'hover:bg-muted dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-500',
                  )}
                  aria-label="Apri menu utente"
                  aria-haspopup="true"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-sky-500 flex items-center justify-center shadow-md shadow-blue-500/30 text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <ChevronDown
                    className={cn(
                      'hidden sm:block h-3.5 w-3.5 text-muted-foreground dark:text-muted-foreground/60 transition-transform',
                      userMenuOpen && 'rotate-180',
                    )}
                  />
                </button>

                {userMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-64 py-1 rounded-xl bg-white dark:bg-slate-900/95 backdrop-blur-xl border border-border dark:border-white/10 shadow-xl z-50"
                    role="menu"
                  >
                    <div className="px-4 py-3 border-b border-border dark:border-white/10">
                      <p className="text-sm font-medium text-foreground dark:text-white truncate">{fullName}</p>
                      <p className="text-[11px] text-blue-600/80 dark:text-blue-300/80 mt-0.5">
                        {isSuperAdmin ? 'Super Administrator' : 'Admin CRM'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        if (surfaceContext.surface === 'crm') {
                          window.location.assign(buildMasterUrl('/account/security'));
                        } else {
                          navigate('/account/security');
                        }
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground dark:text-foreground hover:bg-blue-50 dark:hover:bg-blue-500/10 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      role="menuitem"
                    >
                      <Lock className="h-4 w-4" />
                      <span>Sicurezza</span>
                    </button>
                    <div className="my-1 h-px bg-border dark:bg-white/10" />
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        void handleLogout();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground dark:text-foreground hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      role="menuitem"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className="hidden lg:flex flex-col w-60 shrink-0 sticky top-16 h-[calc(100vh-4rem)] border-r border-border dark:border-white/10 bg-white dark:bg-slate-900/60 p-3 gap-1 overflow-y-auto"
          aria-label="Navigazione CRM"
        >
          {CRM_SECTIONS.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.value;
            return (
              <button
                key={item.value}
                onClick={() => navigate(crmSectionPath(item.value))}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-all',
                  isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-muted-foreground dark:text-muted-foreground/70 hover:bg-muted dark:hover:bg-white/10 hover:text-foreground dark:hover:text-white',
                )}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </aside>

        {/* Mobile section selector */}
        <div className="lg:hidden w-full">
          <nav className="flex gap-1 overflow-x-auto p-2 border-b border-border dark:border-white/10 bg-white dark:bg-slate-900/60" aria-label="Navigazione CRM">
            {CRM_SECTIONS.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.value;
              return (
                <button
                  key={item.value}
                  onClick={() => navigate(crmSectionPath(item.value))}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all',
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-muted-foreground dark:text-muted-foreground/70 hover:bg-muted dark:hover:bg-white/10',
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
